import SoftButton from "components/SoftButton";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import ImportFlowDropzone from "../ImportFlowDropzone";
import { useCallback, useMemo, useState } from "react";
import { useIntegrations } from "hooks/useIntegrations";
import { useRefetchOnFocus } from "hooks/useRefetchOnFocus";
import { Icon } from "@mui/material";
import SoftTypography from "components/SoftTypography";
import SoftBox from "components/SoftBox";
import { insertFlow } from "api/FlowsApi";
import { useAuth0 } from "@auth0/auth0-react";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { createResourceDependencies } from "redux/actions/portal/resourceDependenciesActionCreator";
import { getCopiedFlowDependencies } from "helpers/resourceDependencies";

export default function ImportFlowDialog({
  open,
  onClose,
  onAccept,
  acceptButtonText,
  dialogTitle,
  acceptButtonColor
}) {
  const { integrations, handleFetchIntegrations, handleFetchSharedIntegrations } =
    useIntegrations();
  const [fileToImport, setFileToImport] = useState([]);
  const { user } = useAuth0();
  const dispatch = useDispatch();
  const [fileError, setFileError] = useState(null);
  const refetchIntegrations = useCallback(() => {
    if (!open) return;

    handleFetchIntegrations();
    handleFetchSharedIntegrations();
  }, [open, handleFetchIntegrations, handleFetchSharedIntegrations]);

  useRefetchOnFocus(refetchIntegrations);

  const missingIntegrations = useMemo(() => {
    let missingIntegrations = {};

    if (!fileToImport.length) return null;

    fileToImport.forEach((flow) => {
      try {
        const parsedIntegrations = JSON.parse(flow.integrations);

        parsedIntegrations.forEach((flowIntegration) => {
          if (
            !integrations.find((integration) => integration.id === flowIntegration) &&
            !missingIntegrations[flowIntegration]
          ) {
            missingIntegrations[flowIntegration] = flowIntegration;
          }
        });
      } catch {
        // we dont really do anything
      }
    });

    if (Object.keys(missingIntegrations).length === 0) return null;

    return missingIntegrations;
  }, [integrations, fileToImport]);

  const handleAccept = async () => {
    try {
      if (fileToImport && fileToImport.length > 0) {
        const insertFlowPromises = fileToImport.map(async (file) => {
          const {
            created_date_time,
            id: originalFlowId,
            tenant_id: originalTenantId,
            updated_date_time,
            price,
            publish_status,
            trashed,
            integrations,
            ...restFlow
          } = file;

          const parsedIntegrations = integrations ? JSON.parse(integrations) : [];

          const allowedIntegrations = parsedIntegrations.filter((integration) => {
            return !missingIntegrations || !missingIntegrations[integration];
          });

          const parsedSteps = JSON.parse(restFlow.steps);
          parsedSteps.context.tenant_id = user.stripeId;

          const input = {
            ...restFlow,
            steps: JSON.stringify(parsedSteps),
            integrations: JSON.stringify(allowedIntegrations),
            flow_settings: restFlow?.flow_settings
              ? typeof restFlow.flow_settings === "string"
                ? restFlow.flow_settings
                : JSON.stringify(restFlow?.flow_settings)
              : null
          };

          const insertFlowResponse = await insertFlow(user?.stripeId, input);

          input.tenant_id = user?.stripeId;
          insertFlowResponse.resultObject = Object.assign(
            insertFlowResponse.resultObject || {},
            input
          );
          const newFlowId = insertFlowResponse.resultObject?.id;

          if (newFlowId && originalFlowId) {
            try {
              const importedFlowDependencies = await getCopiedFlowDependencies({
                tenantId: originalTenantId || user?.stripeId,
                originalFlowId,
                newFlowId
              });

              if (importedFlowDependencies.length > 0) {
                dispatch(
                  createResourceDependencies({
                    tenantId: user?.stripeId,
                    dependencies: importedFlowDependencies
                  })
                );
              }
            } catch (e) {
              console.error("Failed to fetch/create dependencies for imported flow", e);
            }
          }

          return insertFlowResponse;
        });

        await Promise.all(insertFlowPromises);

        toast.success(`Successfully imported flow${fileToImport.length > 1 ? "s" : ""}.`);
        setFileToImport([]);
        onAccept();
      }
    } catch (error) {
      console.log(error);
      toast.error(
        `Something went wrong. Please try again importing the flow${
          fileToImport.length > 1 ? "s" : ""
        }.`
      );
      onAccept(false);
    }
  };

  const handleClose = () => {
    setFileToImport([]);
    setFileError(null);
    onClose();
  };

  return (
    <>
      <Dialog
        fullWidth={true}
        maxWidth={"sm"}
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{dialogTitle}</DialogTitle>
        <DialogContent>
          <ImportFlowDropzone
            fileError={fileError}
            setFileError={setFileError}
            setFileToImport={setFileToImport}
          />
          {missingIntegrations && Object.keys(missingIntegrations).length > 0 && (
            <SoftBox className="border-top" sx={{ paddingTop: "20px" }}>
              <SoftBox
                sx={{
                  alignItems: "center",
                  backgroundColor: "#FFEDED",
                  border: "1px solid #E53E3E",
                  display: "flex",
                  borderRadius: "9px",
                  color: "#E53E3E",
                  minHeight: "59px",
                  justifyContent: "center",
                  marginBottom: "20px",
                  padding: "0 24px"
                }}
              >
                <Icon sx={{ fontSize: "16px !important", marginRight: "16px" }}>warning</Icon>
                <SoftTypography
                  sx={{
                    color: "#E53E3E",
                    fontSize: "14px",
                    fontWeight: 600
                  }}
                >
                  Warning: Imported {fileToImport.length > 1 ? "flows" : "flow"} contains one or
                  more integrations that do not exist in this account. These integration references
                  will be removed after importing.
                </SoftTypography>
              </SoftBox>
            </SoftBox>
          )}
        </DialogContent>
        <DialogActions>
          <SoftButton onClick={handleClose} variant="outlined" color="info">
            Cancel
          </SoftButton>
          <SoftButton
            variant="contained"
            color={acceptButtonColor ? acceptButtonColor : "info"}
            onClick={handleAccept}
          >
            {acceptButtonText}
          </SoftButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
